import axios from "axios";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { HiArrowLeft, HiArrowRight } from "react-icons/hi";
import { useNavigate, useParams } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";

const CourseEnrollment = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, setUser } = useAuth();
  const savedLang = localStorage.getItem("lang") || "ar";

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Force Arabic always
    i18n.changeLanguage("ar");

    // Force RTL always
    document.documentElement.dir = "rtl";
    document.body.dir = "rtl";
  }, []);

  // Save course ID to localStorage whenever it changes
  useEffect(() => {
    if (id) {
      localStorage.setItem("lastCourseId", id);
    }
  }, [id]);

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const allCourses = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/courses`, {
          headers: {
            lang: i18n.language,
            Authorization: `Bearer ${user?.token}`,
          },
        });

        const courseData = allCourses.data.data.find((c) => c.id === parseInt(id));
        setCourse(courseData || null);
      } catch (err) {
        if (err.response?.status === 401) {
          setUser(null);
          navigate("/auth/login", { replace: true });
        } else {
          console.error(err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [id, user?.token, i18n.language]);

  const handleStartLearning = () => {
    navigate("/course", { state: { courseData: course } });
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl animate-pulse px-4 py-12">
        <div className="mb-6 h-6 w-48 rounded bg-gray-300" />
        <div className="h-96 rounded bg-gray-200" />
      </div>
    );
  }

  if (!course) return <p className="mt-10 text-center">Course not found.</p>;

  const isEnrolled = course.is_started;
  const isCompleted = course.is_completed;
  const isSurveyCompleted = course.is_survey_completed;

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      {/* Header */}
      <div className="mb-2 flex items-center justify-between">
        <button
          onClick={() => navigate("/?lang=" + savedLang)}
          className="flex items-center gap-2 text-purple-500 hover:underline"
        >
          {i18n.dir() === "ltr" ? <HiArrowLeft /> : <HiArrowRight />}
          {t("backToHomepage")}
        </button>
        {/* <LanguageSwitcher defaultTheme /> */}
      </div>

      {/* Enrollment / Start Learning Banner */}
      <div className="mb-6 rounded-xl bg-purple-50 p-8 text-center shadow-sm">
        <p className="mb-2 font-semibold text-gray-900">
          {isCompleted && isSurveyCompleted
            ? t("youFinishedThisCourse")
            : isEnrolled
              ? t("youAreEnrolled")
              : t("enrollInThisCourse")}
        </p>
        <p className="mb-4 uppercase tracking-widest text-gray-500">{course.name}</p>

        <button
          onClick={handleStartLearning}
          className="mx-auto flex items-center justify-center gap-2 rounded border bg-purple-500 px-6 py-3 font-semibold text-white shadow-sm transition hover:bg-purple-600"
        >
          {isCompleted && isSurveyCompleted
            ? t("courseCompleted")
            : isEnrolled
              ? t("resumeLearning")
              : t("startLearning")}
          {i18n.dir() === "ltr" ? <HiArrowRight /> : <HiArrowLeft />}
        </button>
      </div>

      {/* Course Info */}
      <div className="space-y-6 rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
        {course.description && (
          <div>
            <h3 className="mb-2 text-xl font-semibold">{t("description")}</h3>
            <div
              className="prose mx-auto flex-grow text-right"
              dangerouslySetInnerHTML={{
                __html: course.description.replace(/\r\n/g, "<br />"),
              }}
            />
          </div>
        )}
        {course.learner_achievements && (
          <div>
            <h3 className="mb-2 text-xl font-semibold">{t("learnerAchievements")}</h3>
            <div
              className="prose mx-auto flex-grow text-right"
              dangerouslySetInnerHTML={{
                __html: course.learner_achievements.replace(/\r\n/g, "<br />"),
              }}
            />
          </div>
        )}

        {course.outcomes && (
          <div>
            <h3 className="mb-2 text-xl font-semibold">{t("courseOutcomes")}</h3>
            <div
              className="prose mx-auto flex-grow text-right"
              dangerouslySetInnerHTML={{
                __html: course.outcomes.replace(/\r\n/g, "<br />"),
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseEnrollment;
